<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/#/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Make Payment</span>
            </nav>
            <h1 class="page-title">Make Payment</h1>
            <p class="page-subtitle">Pay your county bills securely using M-Pesa</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                      <line x1="2" y1="10" x2="22" y2="10"></line>
                    </svg>
                  </div>
                  <h3>Payment Portal</h3>
                </div>
                <p class="info-description">
                  Pay for all county services quickly and securely using M-Pesa mobile money.
                </p>

                <div class="paybill-highlight">
                  <span class="highlight-label">M-Pesa Paybill</span>
                  <strong class="highlight-value">{{ paybillNumber }}</strong>
                </div>

                <div class="payment-steps">
                  <h4>How to Pay via M-Pesa</h4>
                  <ol>
                    <li>
                      <span class="step-num">1</span>
                      <div>
                        <strong>Go to M-Pesa</strong>
                        <p>Select Lipa na M-Pesa → Paybill</p>
                      </div>
                    </li>
                    <li>
                      <span class="step-num">2</span>
                      <div>
                        <strong>Enter Business Number</strong>
                        <p>{{ paybillNumber }}</p>
                      </div>
                    </li>
                    <li>
                      <span class="step-num">3</span>
                      <div>
                        <strong>Enter Account Number</strong>
                        <p>Your Invoice Number</p>
                      </div>
                    </li>
                    <li>
                      <span class="step-num">4</span>
                      <div>
                        <strong>Enter Amount & PIN</strong>
                        <p>Confirm payment</p>
                      </div>
                    </li>
                  </ol>
                </div>

                <div class="help-box">
                  <h4>Need Help?</h4>
                  <p>Contact our support team for payment assistance.</p>
                  <a href="/#/contact" class="help-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Contact Support
                  </a>
                </div>
              </div>
            </div>

            <!-- Right Side - Payment Form -->
            <div class="form-panel">
              <!-- Tab Navigation -->
              <div class="tab-navigation">
                <button
                    class="tab-btn"
                    :class="{ active: activeTab === 'lookup' }"
                    @click="activeTab = 'lookup'"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  Find Invoice
                </button>
                <button
                    class="tab-btn"
                    :class="{ active: activeTab === 'status' }"
                    @click="activeTab = 'status'"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Check Status
                </button>
              </div>

              <!-- Find Invoice Tab -->
              <div v-if="activeTab === 'lookup'" class="form-card">
                <div class="form-card-header">
                  <h2>Find Your Invoice</h2>
                  <p>Enter your invoice number to view details and make payment</p>
                </div>

                <div class="form-card-body">
                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">Invoice Number <span class="required">*</span></label>
                      <div class="input-with-btn">
                        <input
                            type="text"
                            v-model="invoiceNumber"
                            class="form-input"
                            placeholder="Enter invoice number (e.g., INV-12345)"
                            @keyup.enter="lookupInvoice"
                        />
                        <button type="button" class="search-btn" @click="lookupInvoice" :disabled="!invoiceNumber || searching">
                          <span v-if="searching" class="spinner small white"></span>
                          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="error" class="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {{ error }}
                  </div>

                  <!-- Invoice Details -->
                  <div v-if="billDetails" class="invoice-result">
                    <div class="result-header">
                      <div class="result-status" :class="getStatusClass(billDetails.status)">
                        <svg v-if="billDetails.status === 'PAID'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {{ billDetails.status || 'UNPAID' }}
                      </div>
                      <span class="result-date">{{ formatDate(billDetails.dateCreated) }}</span>
                    </div>

                    <div class="invoice-card">
                      <div class="invoice-header">
                        <div>
                          <span class="invoice-label">Invoice Number</span>
                          <strong class="invoice-number">{{ billDetails.billNo }}</strong>
                        </div>
                        <div class="invoice-amount-box">
                          <span class="amount-label">{{ billDetails.status === 'PAID' ? 'Amount Paid' : 'Amount Due' }}</span>
                          <strong class="amount-value">KES {{ formatAmount(billDetails.status === 'PAID' ? billDetails.receiptAmount : (billDetails.billBalance || billDetails.detailAmount)) }}</strong>
                        </div>
                      </div>

                      <div class="invoice-details">
                        <div class="detail-row">
                          <span class="detail-label">Customer Name</span>
                          <span class="detail-value">{{ billDetails.names || billDetails.paidBy || billDetails.customerName || '-' }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.customerPhoneNumber">
                          <span class="detail-label">Phone Number</span>
                          <span class="detail-value">{{ billDetails.customerPhoneNumber }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.idNo">
                          <span class="detail-label">ID Number</span>
                          <span class="detail-value">{{ billDetails.idNo }}</span>
                        </div>
                        <div class="detail-row">
                          <span class="detail-label">Description</span>
                          <span class="detail-value">{{ billDetails.feeDescription || billDetails.description || '-' }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.zone">
                          <span class="detail-label">Zone/Location</span>
                          <span class="detail-value">{{ billDetails.zone }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.subCountyName">
                          <span class="detail-label">Sub County / Ward</span>
                          <span class="detail-value">{{ billDetails.wardName ? billDetails.wardName + ', ' : '' }}{{ billDetails.subCountyName }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.transactionCode && billDetails.status === 'PAID'">
                          <span class="detail-label">Transaction Code</span>
                          <span class="detail-value">{{ billDetails.transactionCode }}</span>
                        </div>
                        <div class="detail-row" v-if="billDetails.source && billDetails.status === 'PAID'">
                          <span class="detail-label">Payment Source</span>
                          <span class="detail-value">{{ billDetails.source }}</span>
                        </div>
                      </div>

                      <!-- Bill Items -->
                      <div v-if="billInfo && billInfo.length > 0" class="bill-items">
                        <h4>Invoice Items</h4>
                        <table class="items-table">
                          <thead>
                          <tr>
                            <th>Description</th>
                            <th class="text-center">Qty</th>
                            <th class="text-right">Amount</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="item in billInfo" :key="item.id">
                            <td>{{ item.feeDescription || item.description }}</td>
                            <td class="text-center">{{ item.quantity || 1 }}</td>
                            <td class="text-right">KES {{ formatAmount(item.billTotal || item.detailAmount) }}</td>
                          </tr>
                          </tbody>
                          <tfoot>
                          <tr>
                            <th colspan="2">Total</th>
                            <th class="text-right">KES {{ formatAmount(billDetails.detailAmount) }}</th>
                          </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <!-- Payment Actions -->
                    <div v-if="billDetails.status !== 'PAID' && parseFloat(billDetails.billBalance || billDetails.detailAmount) > 0" class="payment-section">
                      <div class="mpesa-instructions">
                        <div class="mpesa-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                            <line x1="12" y1="18" x2="12.01" y2="18"></line>
                          </svg>
                        </div>
                        <div class="mpesa-content">
                          <h4>Pay via M-Pesa</h4>
                          <p>Use STK Push or manual paybill</p>
                          <div class="mpesa-details">
                            <div class="mpesa-item">
                              <span>Paybill:</span>
                              <strong>{{ paybillNumber }}</strong>
                            </div>
                            <div class="mpesa-item">
                              <span>Account:</span>
                              <strong>{{ billDetails.billNo }}</strong>
                            </div>
                            <div class="mpesa-item">
                              <span>Amount:</span>
                              <strong>KES {{ formatAmount(billDetails.billBalance || billDetails.detailAmount) }}</strong>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- STK Push Payment Form -->
                      <div class="stk-payment-form">
                        <h4 class="form-section-title">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                          </svg>
                          Pay Now (STK Push)
                        </h4>

                        <div class="form-group">
                          <label class="form-label">Amount to Pay</label>
                          <div class="input-with-currency">
                            <span class="currency-prefix">KES</span>
                            <input
                                type="number"
                                v-model="amountToPay"
                                class="form-input with-prefix"
                                placeholder="Enter amount"
                                :max="billDetails.billBalance || billDetails.detailAmount"
                            />
                          </div>
                          <div class="quick-amounts">
                            <button
                                type="button"
                                class="quick-btn"
                                :class="{ active: amountToPay == (billDetails.billBalance || billDetails.detailAmount) }"
                                @click="amountToPay = billDetails.billBalance || billDetails.detailAmount"
                            >
                              Full Amount
                            </button>
                            <button
                                v-if="parseFloat(billDetails.billBalance || billDetails.detailAmount) >= 1000"
                                type="button"
                                class="quick-btn"
                                @click="amountToPay = Math.floor((billDetails.billBalance || billDetails.detailAmount) / 2)"
                            >
                              Half (KES {{ formatAmount(Math.floor((billDetails.billBalance || billDetails.detailAmount) / 2)) }})
                            </button>
                          </div>
                        </div>

                        <div class="form-group">
                          <label class="form-label">M-Pesa Phone Number</label>
                          <input
                              type="tel"
                              v-model="phoneNumber"
                              class="form-input"
                              placeholder="e.g., 07XXXXXXX"
                          />
                          <span class="form-hint">You'll receive an STK push to this number</span>
                        </div>

                        <button
                            type="button"
                            class="btn btn-success btn-block"
                            @click="customerPayBillOnline"
                            :disabled="!amountToPay || !phoneNumber || paymentLoading"
                        >
                          <span v-if="paymentLoading" class="spinner white small"></span>
                          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                          </svg>
                          {{ paymentLoading ? 'Processing...' : `Pay KES ${formatAmount(amountToPay || 0)}` }}
                        </button>
                      </div>

                      <div class="action-buttons">
                        <button type="button" class="btn btn-secondary" @click="printInvoice">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                          </svg>
                          Print Invoice
                        </button>
                        <button type="button" class="btn btn-outline" @click="checkPaymentStatus">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M23 4v6h-6"></path>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                          </svg>
                          Check Status
                        </button>
                      </div>
                    </div>

                    <!-- Paid Status -->
                    <div v-else class="paid-notice">
                      <div class="paid-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h3>Invoice Paid</h3>
                      <p>This invoice has been fully paid. Thank you!</p>
                      <div class="paid-actions">
                        <button type="button" class="btn btn-secondary" @click="printReceipt">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          Print Receipt
                        </button>
                        <button type="button" class="btn btn-outline" @click="resetLookup">
                          Search Another
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else-if="!searching && !error" class="empty-state">
                    <div class="empty-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                    </div>
                    <h3>Enter Invoice Number</h3>
                    <p>Search for your invoice to view details and payment options</p>
                  </div>
                </div>
              </div>

              <!-- Check Status Tab -->
              <div v-else-if="activeTab === 'status'" class="form-card">
                <div class="form-card-header">
                  <h2>Check Payment Status</h2>
                  <p>Verify if your payment has been received</p>
                </div>

                <div class="form-card-body">
                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">Invoice or Receipt Number <span class="required">*</span></label>
                      <div class="input-with-btn">
                        <input
                            type="text"
                            v-model="statusCheckNumber"
                            class="form-input"
                            placeholder="Enter invoice or receipt number"
                            @keyup.enter="checkStatus"
                        />
                        <button type="button" class="search-btn" @click="checkStatus" :disabled="!statusCheckNumber || checkingStatus">
                          <span v-if="checkingStatus" class="spinner small white"></span>
                          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="statusError" class="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {{ statusError }}
                  </div>

                  <!-- Status Result -->
                  <div v-if="statusResult" class="status-result">
                    <div class="status-card" :class="statusResult.status === 'PAID' ? 'success' : 'pending'">
                      <div class="status-icon">
                        <svg v-if="statusResult.status === 'PAID'" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                      <h3>{{ statusResult.status === 'PAID' ? 'Payment Confirmed' : 'Payment Pending' }}</h3>
                      <p v-if="statusResult.status === 'PAID'">Your payment has been received and processed successfully.</p>
                      <p v-else>We haven't received payment for this invoice yet. Please complete payment via M-Pesa.</p>
                    </div>

                    <div class="status-details">
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="label">Invoice Number</span>
                          <strong>{{ statusResult.billNo }}</strong>
                        </div>
                        <div class="detail-item">
                          <span class="label">Amount</span>
                          <strong>KES {{ formatAmount(statusResult.detailAmount) }}</strong>
                        </div>
                        <div class="detail-item" v-if="statusResult.transactionCode && statusResult.status === 'PAID'">
                          <span class="label">Transaction Code</span>
                          <strong>{{ statusResult.transactionCode }}</strong>
                        </div>
                        <div class="detail-item" v-if="statusResult.paidBy && statusResult.status === 'PAID'">
                          <span class="label">Paid By</span>
                          <strong>{{ statusResult.paidBy }}</strong>
                        </div>
                        <div class="detail-item" v-if="statusResult.source && statusResult.status === 'PAID'">
                          <span class="label">Payment Method</span>
                          <strong>{{ statusResult.source }}</strong>
                        </div>
                        <div class="detail-item">
                          <span class="label">{{ statusResult.status === 'PAID' ? 'Date Paid' : 'Date Created' }}</span>
                          <strong>{{ formatDate(statusResult.dateCreated) }}</strong>
                        </div>
                      </div>
                    </div>

                    <div class="status-actions">
                      <button v-if="statusResult.status === 'PAID'" type="button" class="btn btn-primary" @click="printReceiptFromStatus">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                        Print Receipt
                      </button>
                      <button v-else type="button" class="btn btn-primary" @click="viewInvoiceFromStatus">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                          <line x1="2" y1="10" x2="22" y2="10"></line>
                        </svg>
                        View Invoice & Pay
                      </button>
                      <button type="button" class="btn btn-secondary" @click="resetStatus">
                        Check Another
                      </button>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else-if="!checkingStatus && !statusError" class="empty-state">
                    <div class="empty-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <h3>Check Payment Status</h3>
                    <p>Enter your invoice or receipt number to verify payment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Recent Payments Section -->
      <section class="recent-section" v-if="recentPayments.length > 0">
        <div class="container">
          <div class="section-header">
            <h2>Recent Transactions</h2>
            <p>Your latest payment activity</p>
          </div>
          <div class="recent-grid">
            <div v-for="payment in recentPayments" :key="payment.billNo" class="recent-card" @click="viewPayment(payment)">
              <div class="recent-icon" :class="payment.status === 'PAID' ? 'success' : 'pending'">
                <svg v-if="payment.status === 'PAID'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div class="recent-info">
                <strong>{{ payment.billNo }}</strong>
                <span>{{ payment.feeDescription || payment.incomeTypeDescription || 'Payment' }}</span>
              </div>
              <div class="recent-amount">
                <strong>KES {{ formatAmount(payment.detailAmount) }}</strong>
                <span class="recent-status" :class="payment.status === 'PAID' ? 'paid' : 'unpaid'">{{ payment.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Payment Modal -->
    <transition name="modal">
      <div v-if="payConfirmationPop" class="modal-overlay">
        <div class="modal-container payment-modal">
          <!-- Payment Header -->
          <div class="payment-modal-header" :class="paymentReceived ? 'success' : 'pending'">
            <div v-if="!paymentReceived" class="payment-loader">
              <div class="loader-ring"></div>
            </div>
            <div v-else class="payment-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p class="payment-status-text">
              {{ paymentReceived ? 'Payment Successful!' : `Pending ${billDetails?.feeDescription || 'Invoice'} Payment` }}
            </p>
            <h2 class="payment-amount">KES {{ formatAmount(amountToPay) }}</h2>
          </div>

          <!-- Payment Body -->
          <div class="payment-modal-body">
            <!-- Instructions (while waiting) -->
            <div v-if="!paymentReceived" class="payment-instructions">
              <h4>Payment Procedure</h4>
              <p class="instruction-subtitle">Follow the steps below to complete your payment</p>
              <ol>
                <li>
                  <span class="step-num">1</span>
                  Check for a <strong>payment pop up</strong> on your phone
                </li>
                <li>
                  <span class="step-num">2</span>
                  Enter your <strong>M-Pesa PIN</strong> and click OK
                </li>
                <li>
                  <span class="step-num">3</span>
                  Wait for <strong>M-Pesa confirmation SMS</strong>
                </li>
                <li>
                  <span class="step-num">4</span>
                  Wait up to <strong>45 seconds</strong> as we process your transaction
                </li>
              </ol>
              <div class="waiting-indicator">
                <div class="pulse-dot"></div>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>

            <!-- Success Content -->
            <div v-if="paymentReceived" class="payment-success">
              <h4>Thank You {{ transaction.names || 'Customer' }}!</h4>
              <p class="success-message">Payment was successfully received.</p>

              <div class="success-details">
                <div class="success-row">
                  <span>Transaction Code</span>
                  <strong>{{ transaction.transaction_code }}</strong>
                </div>
                <div class="success-row">
                  <span>Bill Number</span>
                  <strong>{{ billDetails.billNo }}</strong>
                </div>
                <div class="success-row">
                  <span>Description</span>
                  <strong>{{ billDetails.feeDescription || billDetails.incomeTypeDescription }}</strong>
                </div>
                <div class="success-row">
                  <span>Amount Paid</span>
                  <strong>KES {{ formatAmount(amountToPay) }}</strong>
                </div>
              </div>

              <div class="success-actions">
                <button @click="printReceiptAfterPayment" class="btn btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Receipt
                </button>
                <button @click="closePaymentModal" class="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, paysol, execute, execute2 } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'PaymentPage',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      activeTab: 'lookup',
      county: null,
      bank: null,
      payBill: null,
      invoiceNumber: '',
      searching: false,
      error: '',
      billDetails: null,
      billInfo: [],
      statusCheckNumber: '',
      checkingStatus: false,
      statusError: '',
      statusResult: null,
      recentPayments: [],
      // Payment fields
      phoneNumber: '',
      amountToPay: null,
      paymentLoading: false,
      payConfirmationPop: false,
      paymentReceived: false,
      transaction: {
        id: null,
        transaction_code: null,
        names: null,
        msisdn: null
      },
      receiptDetails: {
        receiptNo: null
      }
    };
  },

  computed: {
    paybillNumber() {
      return this.bank?.payBill || this.payBill?.shortCode || this.county?.paybillNumber || 'XXX XXX';
    }
  },

  mounted() {
    this.getCounty();
    this.checkUrlParams();
  },

  watch: {
    // Watch for route changes to handle navigation with different bill numbers
    '$route.params.billNo': {
      handler(newBillNo) {
        if (newBillNo) {
          this.invoiceNumber = newBillNo;
          this.lookupInvoice();
        }
      },
      immediate: false
    }
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('en-KE', { minimumFractionDigits: 0 });
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    getStatusClass(status) {
      if (status === 'PAID') return 'paid';
      if (status === 'PARTIAL') return 'partial';
      return 'unpaid';
    },

    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (e) { console.error(e); }
    },

    checkUrlParams() {
      // Check for route params first (e.g., /payment/PKN042802303369)
      const routeBillNo = this.$route.params.billNo;

      // Then check for query params (e.g., /payment?billNo=PKN042802303369)
      const urlParams = new URLSearchParams(window.location.search);
      const queryBillNo = urlParams.get('billNo') || urlParams.get('invoice');

      // Route params take priority over query params
      const billNo = routeBillNo || queryBillNo;

      if (billNo) {
        this.invoiceNumber = billNo;
        this.lookupInvoice();
      }
    },

    async lookupInvoice() {
      if (!this.invoiceNumber) return;
      this.searching = true;
      this.error = '';
      this.billDetails = null;
      this.billInfo = [];

      try {
        const data = new FormData();
        data.append("function", "getBill");
        data.append("billNo", this.invoiceNumber.trim());
        const res = await execute(data, biller);

        if (res.data.success) {
          this.billDetails = res.data.data.billDetails;
          this.billInfo = res.data.data.billInfo || [];
          this.bank = res.data.data.bank;
          this.payBill = res.data.data.payBill;
          this.amountToPay = this.billDetails.billBalance || this.billDetails.detailAmount;
          if (res.data.data.county) {
            this.county = res.data.data.county;
          }
          this.addToRecent(this.billDetails);
        } else {
          this.error = res.data.message || 'Invoice not found. Please check the number and try again.';
        }
      } catch (e) {
        this.error = 'Error searching for invoice. Please try again.';
        console.error(e);
      } finally {
        this.searching = false;
      }
    },

    async checkStatus() {
      if (!this.statusCheckNumber) return;
      this.checkingStatus = true;
      this.statusError = '';
      this.statusResult = null;

      try {
        const data = new FormData();
        data.append("function", "getBill");
        data.append("billNo", this.statusCheckNumber.trim());
        const res = await execute(data, biller);

        if (res.data.success) {
          this.statusResult = res.data.data.billDetails;
          this.bank = res.data.data.bank;
          this.payBill = res.data.data.payBill;
          if (res.data.data.county) {
            this.county = res.data.data.county;
          }
        } else {
          this.statusError = res.data.message || 'Invoice not found. Please check the number and try again.';
        }
      } catch (e) {
        this.statusError = 'Error checking status. Please try again.';
        console.error(e);
      } finally {
        this.checkingStatus = false;
      }
    },

    checkPaymentStatus() {
      this.activeTab = 'status';
      this.statusCheckNumber = this.billDetails?.billNo || '';
      if (this.statusCheckNumber) {
        this.checkStatus();
      }
    },

    addToRecent(bill) {
      if (!bill) return;
      const exists = this.recentPayments.find(p => p.billNo === bill.billNo);
      if (!exists) {
        this.recentPayments.unshift(bill);
        if (this.recentPayments.length > 5) {
          this.recentPayments.pop();
        }
      }
    },

    viewPayment(payment) {
      this.activeTab = 'lookup';
      this.invoiceNumber = payment.billNo;
      this.lookupInvoice();
    },

    viewInvoiceFromStatus() {
      this.activeTab = 'lookup';
      this.invoiceNumber = this.statusResult?.billNo || '';
      if (this.invoiceNumber) {
        this.lookupInvoice();
      }
    },

    printInvoice() {
      if (this.billDetails?.billNo) {
        localStorage.setItem("params", JSON.stringify({ billNo: this.billDetails.billNo }));
        const routeData = this.$router.resolve({ name: 'bill' });
        window.open(routeData.href, '_blank');
      }
    },

    printReceipt() {
      if (!this.billDetails?.transactionCode && !this.billDetails?.receiptNo) {
        this.error = "Receipt number not available";
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.billDetails.transactionCode || this.billDetails.receiptNo,
        billNo: this.billDetails.billNo
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    printReceiptFromStatus() {
      if (!this.statusResult?.transactionCode && !this.statusResult?.receiptNo) {
        this.statusError = "Receipt number not available";
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.statusResult.transactionCode || this.statusResult.receiptNo,
        billNo: this.statusResult.billNo
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    customerPayBillOnline() {
      if (!this.phoneNumber) {
        this.error = "Please enter your M-Pesa phone number";
        return;
      }

      if (!this.amountToPay || this.amountToPay <= 0) {
        this.error = "Please enter a valid amount";
        return;
      }

      this.error = '';
      this.paymentLoading = true;
      this.payConfirmationPop = true;

      const data = new FormData();
      data.append("function", "customerPayBillOnline");
      data.append("payBillNumber", this.bank?.payBill || this.payBill?.shortCode);
      data.append("amount", this.amountToPay);
      data.append("accountReference", this.billDetails.billNo);
      data.append("transactionDesc", this.billDetails.billNo);
      data.append("phoneNumber", this.phoneNumber);
      data.append("token", "im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ");

      execute2(data, paysol)
          .then((res) => {
            this.paymentLoading = false;
            if (res.data.success) {
              const intervalId = setInterval(() => {
                this.checkPaymentStatus2()
                    .then((result) => {
                      if (result) {
                        clearInterval(intervalId);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
              }, 2000);
            } else {
              this.payConfirmationPop = false;
              this.error = res.data.message || 'Failed to initiate payment. Please try again.';
            }
          })
          .catch((e) => {
            this.paymentLoading = false;
            this.payConfirmationPop = false;
            this.error = e.message || 'Payment request failed. Please try again.';
          });
    },

    checkPaymentStatus2() {
      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.billDetails.billNo);

      return new Promise((resolve, reject) => {
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.paymentLoading = false;
                this.transaction = res.data.data.transaction;
                this.receiptDetails = res.data.data.receipt?.receiptDetails || {};
                this.paymentReceived = true;
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((error) => {
              reject(error);
            });
      });
    },

    closePaymentModal() {
      this.payConfirmationPop = false;
      this.paymentReceived = false;
      this.paymentLoading = false;
      // Refresh bill details
      this.lookupInvoice();
    },

    printReceiptAfterPayment() {
      const receiptNo = this.transaction?.transaction_code || this.receiptDetails?.receiptNo;

      if (!receiptNo) {
        this.error = "Receipt number not available yet. Please try again.";
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: receiptNo,
        billNo: this.billDetails.billNo
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    resetLookup() {
      this.invoiceNumber = '';
      this.billDetails = null;
      this.billInfo = [];
      this.error = '';
      this.phoneNumber = '';
      this.amountToPay = null;

      // Clear URL params if present
      if (this.$route.params.billNo) {
        this.$router.push({ name: 'payment' });
      }
    },

    resetStatus() {
      this.statusCheckNumber = '';
      this.statusResult = null;
      this.statusError = '';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { padding-bottom: 80px; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 50%, #0a3459 100%); padding: 120px 0 60px /* clear fixed header */; position: relative; overflow: hidden; }
.page-hero::before { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 500px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(30%, -30%); }
.hero-content { text-align: center; position: relative; z-index: 1; }

.breadcrumb-nav { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; font-size: 14px; }

.page-title { font-size: 36px; font-weight: 800; color: white; margin: 0 0 12px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 16px; color: rgba(255,255,255,0.8); margin: 0; }

/* Content */
.content-section { margin-top: -30px; position: relative; z-index: 10; }
.content-grid { display: grid; grid-template-columns: 320px 1fr; gap: 32px; align-items: start; }

/* Info Panel */
.info-panel { position: sticky; top: 24px; }
.info-card { background: white; border-radius: 16px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; }
.info-card-header h3 { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0; }
.info-description { font-size: 14px; color: #64748b; line-height: 1.6; margin-bottom: 24px; }

.paybill-highlight { background: linear-gradient(135deg, #059669 0%, #047857 100%); border-radius: 12px; padding: 20px; text-align: center; margin-bottom: 24px; }
.highlight-label { display: block; font-size: 12px; color: rgba(255,255,255,0.8); margin-bottom: 4px; }
.highlight-value { display: block; font-size: 32px; font-weight: 800; color: white; letter-spacing: 2px; }

.payment-steps { margin-bottom: 24px; }
.payment-steps h4 { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 16px; }
.payment-steps ol { list-style: none; padding: 0; margin: 0; }
.payment-steps li { display: flex; gap: 14px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; }
.payment-steps li:last-child { border-bottom: none; }
.step-num { width: 24px; height: 24px; background: #0f4c81; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.payment-steps li strong { display: block; font-size: 13px; font-weight: 600; color: #1e293b; }
.payment-steps li p { font-size: 12px; color: #64748b; margin: 2px 0 0; }

.help-box { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 12px; padding: 20px; }
.help-box h4 { font-size: 14px; font-weight: 700; color: #1e3a5f; margin: 0 0 8px; }
.help-box p { font-size: 13px; color: #475569; margin: 0 0 16px; }
.help-link { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #0f4c81; text-decoration: none; }
.help-link:hover { text-decoration: underline; }

/* Tabs */
.tab-navigation { display: flex; gap: 8px; margin-bottom: 24px; }
.tab-btn { display: flex; align-items: center; gap: 8px; padding: 14px 24px; background: white; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 14px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s; }
.tab-btn:hover { border-color: #0f4c81; color: #0f4c81; }
.tab-btn.active { background: #0f4c81; border-color: #0f4c81; color: white; }

/* Form Card */
.form-panel { }
.form-card { background: white; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 28px 32px; border-bottom: 1px solid #e2e8f0; }
.form-card-header h2 { font-size: 24px; font-weight: 800; color: #1e293b; margin: 0 0 6px; }
.form-card-header p { font-size: 14px; color: #64748b; margin: 0; }
.form-card-body { padding: 32px; }

/* Search Form */
.search-form { margin-bottom: 24px; }
.form-group { }
.form-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px; }
.required { color: #ef4444; }
.input-with-btn { display: flex; gap: 12px; }
.form-input { flex: 1; width: 100%; padding: 14px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 15px; font-family: inherit; transition: all 0.2s; background: white; }
.form-input:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1); }
.form-input::placeholder { color: #94a3b8; }
.search-btn { width: 52px; height: 52px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border: none; border-radius: 10px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.search-btn:hover:not(:disabled) { transform: scale(1.05); }
.search-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Error */
.error-message { display: flex; align-items: center; gap: 12px; padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 14px; margin-bottom: 20px; }

/* Invoice Result */
.invoice-result { }
.result-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.result-status { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 700; text-transform: uppercase; }
.result-status.paid { background: #d1fae5; color: #059669; }
.result-status.unpaid { background: #fef3c7; color: #d97706; }
.result-status.partial { background: #dbeafe; color: #2563eb; }
.result-date { font-size: 13px; color: #64748b; }

.invoice-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; }
.invoice-header { display: flex; justify-content: space-between; align-items: center; padding: 24px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; }
.invoice-label { display: block; font-size: 12px; opacity: 0.8; }
.invoice-number { display: block; font-size: 20px; font-weight: 800; margin-top: 4px; }
.invoice-amount-box { text-align: right; }
.amount-label { display: block; font-size: 12px; opacity: 0.8; }
.amount-value { display: block; font-size: 24px; font-weight: 800; margin-top: 4px; }

.invoice-details { padding: 20px 24px; }
.detail-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e2e8f0; }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-size: 13px; color: #64748b; }
.detail-value { font-size: 14px; font-weight: 600; color: #1e293b; text-align: right; }

.bill-items { padding: 0 24px 24px; }
.bill-items h4 { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 12px; }
.items-table { width: 100%; border-collapse: collapse; background: white; border-radius: 10px; overflow: hidden; }
.items-table th, .items-table td { padding: 12px 16px; text-align: left; font-size: 13px; }
.items-table thead { background: #f1f5f9; }
.items-table thead th { font-weight: 600; color: #475569; }
.items-table tbody tr { border-bottom: 1px solid #e2e8f0; }
.items-table tbody td { color: #1e293b; }
.items-table tfoot { background: #0f4c81; color: white; }
.items-table tfoot th { font-weight: 700; }
.text-right { text-align: right; }
.text-center { text-align: center; }

/* Payment Actions */
.payment-actions { margin-top: 24px; }
.mpesa-instructions { display: flex; gap: 20px; padding: 24px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 14px; margin-bottom: 20px; }
.mpesa-icon { width: 64px; height: 64px; background: #fbbf24; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.mpesa-content h4 { font-size: 16px; font-weight: 700; color: #92400e; margin: 0 0 4px; }
.mpesa-content > p { font-size: 13px; color: #a16207; margin: 0 0 16px; }
.mpesa-details { display: flex; gap: 24px; flex-wrap: wrap; }
.mpesa-item { }
.mpesa-item span { display: block; font-size: 11px; color: #92400e; text-transform: uppercase; }
.mpesa-item strong { font-size: 16px; color: #78350f; }

.action-buttons { display: flex; gap: 12px; }

/* Paid Notice */
.paid-notice { text-align: center; padding: 40px 20px; margin-top: 24px; background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); border-radius: 16px; }
.paid-icon { width: 80px; height: 80px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; color: #059669; box-shadow: 0 4px 15px rgba(5, 150, 105, 0.2); }
.paid-notice h3 { font-size: 22px; font-weight: 800; color: #059669; margin: 0 0 8px; }
.paid-notice p { font-size: 14px; color: #047857; margin: 0 0 24px; }
.paid-actions { display: flex; gap: 12px; justify-content: center; }

/* Empty State */
.empty-state { text-align: center; padding: 60px 20px; }
.empty-icon { width: 100px; height: 100px; background: #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; color: #94a3b8; }
.empty-state h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.empty-state p { font-size: 14px; color: #64748b; margin: 0; }

/* Status Result */
.status-result { }
.status-card { text-align: center; padding: 32px; border-radius: 16px; margin-bottom: 24px; }
.status-card.success { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); }
.status-card.pending { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); }
.status-icon { width: 80px; height: 80px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
.status-card.success .status-icon { color: #059669; }
.status-card.pending .status-icon { color: #d97706; }
.status-card h3 { font-size: 20px; font-weight: 800; margin: 0 0 8px; }
.status-card.success h3 { color: #059669; }
.status-card.pending h3 { color: #92400e; }
.status-card p { font-size: 14px; margin: 0; }
.status-card.success p { color: #047857; }
.status-card.pending p { color: #a16207; }

.status-details { background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
.detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.detail-item { }
.detail-item .label { display: block; font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.detail-item strong { font-size: 15px; color: #1e293b; }

.status-actions { display: flex; gap: 12px; justify-content: center; }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.3s; border: none; font-family: inherit; }
.btn-primary { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; box-shadow: 0 4px 15px rgba(15, 76, 129, 0.3); }
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(15, 76, 129, 0.4); }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn-outline { background: transparent; color: #0f4c81; border: 2px solid #0f4c81; }
.btn-outline:hover { background: #0f4c81; color: white; }

/* Spinner */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.small { width: 18px; height: 18px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Recent Section */
.recent-section { padding: 60px 0 0; }
.section-header { margin-bottom: 24px; }
.section-header h2 { font-size: 24px; font-weight: 800; color: #1e293b; margin: 0 0 8px; }
.section-header p { font-size: 14px; color: #64748b; margin: 0; }

.recent-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
.recent-card { display: flex; align-items: center; gap: 16px; padding: 20px; background: white; border: 1px solid #e2e8f0; border-radius: 14px; cursor: pointer; transition: all 0.2s; }
.recent-card:hover { border-color: #0f4c81; box-shadow: 0 4px 15px rgba(15, 76, 129, 0.1); }
.recent-icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.recent-icon.success { background: #d1fae5; color: #059669; }
.recent-icon.pending { background: #fef3c7; color: #d97706; }
.recent-info { flex: 1; }
.recent-info strong { display: block; font-size: 14px; font-weight: 700; color: #1e293b; }
.recent-info span { font-size: 12px; color: #64748b; }
.recent-amount { text-align: right; }
.recent-amount strong { display: block; font-size: 15px; font-weight: 700; color: #1e293b; }
.recent-status { font-size: 11px; font-weight: 600; text-transform: uppercase; }
.recent-status.paid { color: #059669; }
.recent-status.unpaid { color: #d97706; }

/* Responsive */
@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
}

@media (max-width: 768px) {
  .page-title { font-size: 28px; }
  .form-card-body { padding: 24px; }
  .invoice-header { flex-direction: column; gap: 16px; text-align: center; }
  .invoice-amount-box { text-align: center; }
  .mpesa-instructions { flex-direction: column; text-align: center; }
  .mpesa-icon { margin: 0 auto; }
  .mpesa-details { justify-content: center; }
  .action-buttons { flex-direction: column; }
  .paid-actions { flex-direction: column; }
  .status-actions { flex-direction: column; }
  .detail-grid { grid-template-columns: 1fr; }
  .tab-navigation { flex-direction: column; }
  .input-with-btn { flex-direction: column; }
  .search-btn { width: 100%; height: 48px; }
  .quick-amounts { flex-direction: column; }
  .success-actions { flex-direction: column; }
}

/* Payment Section */
.payment-section { margin-top: 24px; }
.stk-payment-form { background: #f8fafc; border-radius: 14px; padding: 24px; margin: 20px 0; }
.form-section-title { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 20px; }
.form-group { margin-bottom: 20px; }
.form-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px; }
.form-hint { display: block; font-size: 12px; color: #64748b; margin-top: 6px; }

.input-with-currency { position: relative; display: flex; }
.currency-prefix { position: absolute; left: 0; top: 0; bottom: 0; width: 56px; background: #e2e8f0; border: 1.5px solid #e2e8f0; border-right: none; border-radius: 10px 0 0 10px; display: flex; align-items: center; justify-content: center; font-weight: 600; color: #64748b; font-size: 14px; }
.form-input.with-prefix { padding-left: 68px; border-radius: 10px; }

.quick-amounts { display: flex; gap: 10px; margin-top: 12px; }
.quick-btn { flex: 1; padding: 10px 16px; background: white; border: 2px solid #e2e8f0; border-radius: 8px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s; }
.quick-btn:hover { border-color: #0f4c81; color: #0f4c81; background: #f0f9ff; }
.quick-btn.active { background: #0f4c81; border-color: #0f4c81; color: white; }

.btn-success { background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3); }
.btn-success:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4); }
.btn-block { width: 100%; }

/* Payment Modal */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 24px; backdrop-filter: blur(4px); }
.modal-container { background: white; border-radius: 20px; max-width: 440px; width: 100%; overflow: hidden; }
.payment-modal-header { padding: 40px 32px; text-align: center; color: white; }
.payment-modal-header.pending { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); }
.payment-modal-header.success { background: linear-gradient(135deg, #059669 0%, #047857 100%); }

.payment-loader { margin-bottom: 20px; }
.loader-ring { width: 60px; height: 60px; border: 4px solid rgba(255,255,255,0.2); border-top-color: white; border-radius: 50%; margin: 0 auto; animation: spin 1s linear infinite; }

.payment-success-icon { width: 80px; height: 80px; background: rgba(255,255,255,0.2); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
.payment-status-text { font-size: 14px; opacity: 0.9; margin-bottom: 8px; }
.payment-amount { font-size: 36px; font-weight: 800; margin: 0; }

.payment-modal-body { padding: 32px; }
.payment-instructions h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.instruction-subtitle { font-size: 13px; color: #64748b; margin-bottom: 20px; }
.payment-instructions ol { list-style: none; padding: 0; margin: 0 0 24px; }
.payment-instructions li { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #475569; }
.payment-instructions li:last-child { border-bottom: none; }
.step-num { width: 24px; height: 24px; background: #eff6ff; color: #2563eb; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }

.waiting-indicator { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; background: #fffbeb; border-radius: 10px; font-size: 14px; color: #92400e; }
.pulse-dot { width: 10px; height: 10px; background: #f59e0b; border-radius: 50%; animation: pulse 1.5s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.2); } }

.payment-success h4 { font-size: 18px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.success-message { font-size: 14px; color: #64748b; margin-bottom: 20px; }
.success-details { background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 24px; text-align: left; }
.success-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
.success-row:last-child { border-bottom: none; }
.success-row span { color: #64748b; }
.success-row strong { color: #1e293b; }
.success-actions { display: flex; gap: 12px; }
.success-actions .btn { flex: 1; }

/* Modal Transitions */
.modal-enter-active, .modal-leave-active { transition: all 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-container, .modal-leave-to .modal-container { transform: scale(0.95) translateY(20px); }
</style>